<template>
    <div>
        <list class="m-0 p-0" v-model="questions" :columns="columns" :pagination="pagination"
              :sort-change="sortChange"
              :actions="actions"
              :edit="(props) => edit(props.row)"
              :remove="(props) => confirmAndDelete(props.row)"
              :title="$t('Question List')"
        />
        <modal v-if="question" v-model="showDialog" :title="question._id ? $t('Edit Question') : $t('Create Question')" :submit="save" :cancel="cancel" submit-text="Save">
            <template slot="body">
                <fg-input :label="$t('Type')" name="type" v-if="question._id === null" required v-model="question.type">
                    <el-select size="large"
                               class="select-primary"
                               placeholder="Type"
                               v-model="question.type">
                        <el-option v-for="option in ['slider', 'text']"
                                   :value="option"
                                   :label="$t(option)"
                                   :key="option">
                        </el-option>
                    </el-select>
                </fg-input>
                <language-picker v-model="question.title" type="question">
                    <template slot="body" slot-scope="props">
                        <fg-input :name="`title${props.index}`"
                                  :label="$t('Title')"
                                  autocomplete="off"
                                  required
                                  :error="getError(`title${props.index}`)"
                                  v-model="question.title[props.index].text"
                                  v-validate="validations.title[props.index]"
                                  :placeholder="$t('Title')">
                        </fg-input>
                        <fg-input name="text"
                                  :label="$t('Text')"
                                  autocomplete="off"
                                  v-model="question.text[props.index].text"
                                  :placeholder="$t('Text')">
                        </fg-input>
                        <div v-if="question.type === 'slider'">
                            <fg-input name="minimumText"
                                      :label="$t('Text slider scale \'minimum\' (red)')"
                                      autocomplete="off"
                                      v-model="question.minimumText[props.index].text"
                                      :placeholder="$t('Minimum Text')">
                            </fg-input>
                            <fg-input name="maximumText"
                                      :label="$t('Text slider scale \'maximum\' (green)')"
                                      autocomplete="off"
                                      v-model="question.maximumText[props.index].text"
                                      :placeholder="$t('Maximum Text')">
                            </fg-input>
                        </div>
                    </template>
                </language-picker>
            </template>
        </modal>
    </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import BaseList from '../../Views/BaseList'
  import { Input, Button, Select, Option} from 'element-ui'
  import Modal from '../../Components/BasicModal'
  import List from '../../Components/BasicList'
  import LanguagePicker from '../../Components/BasicLanguagePicker'
  import QuestionModel from 'src/store/models/Question'

  export default {
    extends: BaseList,
    name: 'QuestionList',
    components: {
      [Input.name]: Input,
      [Button.name]: Button,
      [Option.name]: Option,
      [Select.name]: Select,
      List, Modal, LanguagePicker
    },
    data () {
      return {
        showDialog: false,
        question: null,
        sf: 'createdAt',
        so: 'desc',
        columns: [
          { name: 'title', text: 'Title', formatter: this.languageFormatter, sortable: true, align: 'left' },
          { name: 'text', text: 'Text', formatter: this.languageFormatter, sortable: true, align: 'left' },
          { name: 'type', text: 'Type', formatter: this.typeFormatter, sortable: true, align: 'left' }
        ],
        actions: [
          { label: 'Add Question', click: this.add }
        ],
        validations: {
          title: {
            0: {
              required: true,
              min: 5
            }
          }
        }
      }
    },
    methods: {
      ...mapActions('pagination/question', ['paginate']),
      languageFormatter: function (row, column, cellValue) {
        return this.$options.filters.languageString(cellValue)
      },
      typeFormatter: function (row) {
        return this.$t(row.type)
      },
      fetchCurrentPage: async function () {
        try {
          const newPage = parseInt(this.$route.query.page, 10) || 1
          await QuestionModel.deleteAll()
          const result = await QuestionModel.api().get(`/questionCategory/${this.categoryId}/question`, {
            dataKey: 'items',
            params: { search: this.query, sf: this.sf, so: this.so, l: this.pagination.perPage, lo: newPage - 1 }
          })
          if (result.response && result.response.data && result.response.data.total) {
            this.paginate({
              perPage: this.pagination.perPage,
              currentPage: newPage,
              total: result.response.data.total
            })
          }
        } catch (e) { console.log('e', e)}
      },
      sortChange: function ({ column, prop, order }) {
        this.sf = prop || 'createdAt'
        this.so = (order || 'descending') === 'ascending' ? 'asc' : 'desc'
        this.fetchCurrentPage()
      },
      changePage (val) {
        const page = val > 1 ? val : undefined
        this.$router.push({ ...this.$router.currentRoute, query: { ...this.$route.query, page } })
      },
      confirmAndDelete: function (question) {
        this.$dialog.confirm(this.$t(`Confirm`), this.$t('Are you sure to delete {question}?', { question:  this.$options.filters.languageString(question.title)}), this.$t('Delete'))
          .then(async () => {
            await QuestionModel.api().delete(`/questionCategory/${this.categoryId}/question/${question._id}`)
            await this.fetchCurrentPage()
          })
      },
      getError (fieldName) {
        return this.$t(this.errors.first(fieldName))
      },
      add: async function () {
        this.question = new QuestionModel(QuestionModel.defaults(this.$auth.user.customer.settings.contract.enabledLanguages))
        this.showDialog = true
      },
      edit: async function (question) {
        this.question = question
        this.showDialog = true
      },
      save: async function () {
        const isValid = await this.$validator.validateAll()
        if (!isValid) {
          return
        }

        const { type, title, text, minimumText, maximumText } = this.question
        const data = { type, title, text, minimumText, maximumText }
        try {
          if (this.question._id) {
            await QuestionModel.api().put(`/questionCategory/${this.categoryId}/question/${this.question._id}`, data, { save: false })
            this.$notify({ message: this.$t('Successfully saved.'), type: 'success' })
          } else {
            const result = await QuestionModel.api().post(`/questionCategory/${this.categoryId}/question`, data, { save: false })
            if (result.response.data._id) {
              this.$notify({ message: this.$t('Successfully saved.'), type: 'success' })
            }
          }
          this.showDialog = false
          await this.fetchCurrentPage()
        } catch (e) {
          this.$notify({ message: e.message, type: 'danger' })
        }
      },
      cancel: function () {
        this.showDialog = false
      }
    },
    computed: {
      questions: () => QuestionModel.all(),
      ...mapState('pagination', {
        pagination: (pagination) => pagination.question
      }),
      categoryId: function () {
        return this.$route.params.id || ''
      }
    },
    async mounted () {
      await this.fetchCurrentPage()
    },
    watch: {
      '$route.query.page' (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.fetchCurrentPage()
        }
      }
    }
  }
</script>
